import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import KitLink from './KitLink';
import { breakpoints, fontWeight, spacing } from '../common/theme/constants';
import { TranslateFunc } from '../common/types';

export interface KitHttpErrorProps extends PropsWithChildren {
  t: TranslateFunc;
  links?: KitHttpErrorLinksProps[];
  httpStatus: string;
}

export interface KitHttpErrorLinksProps {
  label: string;
  url: string;
}

const ErrorPageContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${spacing.absolute.xxl}px;

  @media all and (max-width: ${breakpoints.sm}px) {
    padding: ${spacing.absolute.xxl}px ${spacing.absolute.l}px;
  }
`;

const StyledGraphic = styled.div`
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  height: 50vh;
  max-height: 180px;
  width: 80%;
  margin: ${spacing.absolute.xxl}px 0 0;
  @media all and (max-width: ${breakpoints.sm}px) {
    margin: ${spacing.absolute.l}px 0 0;
  }
`;

const ErrorPageText = styled.h1`
  font-size: ${({ theme }) => theme.headingFont.fontSize.text_20}rem;
  font-weight: ${fontWeight.normal};
  text-align: center;
`;

const generateError404Img = () => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='268' height='132'>
      <g fill='none' fillRule='evenodd'>
        <path
          stroke='#CDCDCD'
          strokeWidth='2'
          d='M118.894 116.564c-17.993 1.015-30.894 3.77-30.894 7.01 0 4.101 20.682 7.426 46.195 7.426 25.514 0 46.196-3.325 46.196-7.427 0-3.095-11.779-5.748-28.524-6.863'
        />
        <path
          fill='#9B9B9B'
          fillRule='nonzero'
          stroke='#4A4A4A'
          strokeLinejoin='round'
          strokeWidth='3'
          d='M133.659 1.5l.604.006c14.21.225 25.911 5.792 34.174 14.338 8.875 9.18 13.784 21.813 13.542 35.01-.232 15.986-11.05 31.548-18.474 40.595-4.87 5.937-14.225 14.697-28.077 26.266-.33.276-.658.549-.985.818l-.963-.838c-15.46-13.478-24.993-22.018-28.61-25.61l-.24-.24a23.687 23.687 0 01-.986-1.04c-.32-.365-.75-.81-1.262-1.344-4.685-4.87-15.882-16.524-15.882-38.63 0-12.806 5.49-25.672 14.552-35.045 8.487-8.778 20.09-14.489 33.211-14.28l-.604-.006z'
        />
        <path
          fill='#DEDBDB'
          fillRule='nonzero'
          stroke='#9B9B9B'
          strokeLinejoin='round'
          strokeWidth='3'
          d='M231.012 25.5l.509.004c10.119.16 18.449 4.138 24.332 10.237 6.317 6.549 9.813 15.562 9.641 24.975-.164 11.364-7.816 22.438-13.083 28.872-3.434 4.195-10.026 10.387-19.792 18.562-.331.278-.66.552-.986.82l-.964-.84c-10.794-9.433-17.484-15.441-20.081-18.016l-.216-.215a17.03 17.03 0 01-.714-.754c-.22-.253-.518-.562-.873-.931-3.33-3.47-11.285-11.772-11.285-27.52 0-9.132 3.903-18.309 10.351-24.994 6.049-6.27 14.32-10.345 23.67-10.196l-.509-.004zM36.012 25.5l.509.004c10.119.16 18.449 4.138 24.332 10.237 6.317 6.549 9.813 15.562 9.641 24.975-.164 11.364-7.816 22.438-13.083 28.872-3.434 4.195-10.026 10.387-19.792 18.562-.331.278-.66.552-.986.82l-.964-.84c-10.794-9.433-17.484-15.441-20.081-18.016l-.216-.215a17.03 17.03 0 01-.714-.754c-.22-.253-.518-.562-.873-.931C10.455 84.744 2.5 76.442 2.5 60.694c0-9.132 3.903-18.309 10.351-24.994 6.049-6.27 14.32-10.345 23.67-10.196l-.509-.004z'
        />
      </g>
    </svg>
  );
};

const Error403 = ({ children, t }) => (
  <>
    <ErrorPageText>{t('kit:http_errors.403.title')}</ErrorPageText>
    {children}
  </>
);

const Error404 = ({ children, links, t }) => (
  <>
    <ErrorPageText>{t('kit:http_errors.404.title')}</ErrorPageText>
    <ErrorPageText>{t('kit:http_errors.404.page_suggestions')}</ErrorPageText>

    {links.map(({ label, url }) => (
      <ErrorPageText key={`${label}-${url}`}>
        <KitLink href={url} key={`link-${url}`} t={t}>
          {label}
        </KitLink>
      </ErrorPageText>
    ))}
    <StyledGraphic>{generateError404Img()}</StyledGraphic>
    {children}
  </>
);

const Error500 = ({ children, t }) => (
  <>
    <ErrorPageText>{t('kit:http_errors.500.title')}</ErrorPageText>
    {children}
    <ErrorPageText>{t('kit:http_errors.500.subtitle')}</ErrorPageText>
  </>
);

const errorComponentMap = {
  403: Error403,
  404: Error404,
  500: Error500,
};

const KitHttpError: React.FC<KitHttpErrorProps> = ({ children, httpStatus, t, links }) => {
  const ErrorComponent = errorComponentMap[httpStatus];

  return (
    <ErrorPageContent>
      <ErrorComponent links={links} t={t}>
        {children}
      </ErrorComponent>
    </ErrorPageContent>
  );
};

export default KitHttpError;
